'use client';

import { useCallback, useEffect, useState } from 'react';

/**
 * Converts date object to relative time from now. Value gets updated every second.
 * @example
 * const date = new Date();
 * const timeAgo = useTimeAgo(date);
 * console.log(timeAgo); // just now
 * // one minute later
 * console.log(timeAgo); // 1 minute ago
 * // 2 hours later
 * console.log(timeAgo); // 2 hours ago
 * @param date
 * @returns relative time from now
 */
export function useTimeAgo(date: Date, ms = 1000) {
  const [timeAgo, setTimeAgo] = useState<string>('');

  const calculate = useCallback(() => {
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    const intervals: { [key: string]: number } = {
      year: 31536000,
      month: 2592000,
      week: 604800,
      day: 86400,
      hour: 3600,
      minute: 60,
    };
    for (const [unit, secondsInUnit] of Object.entries(intervals)) {
      const count = Math.floor(seconds / secondsInUnit);
      if (count >= 1) {
        return `${count} ${unit}${count !== 1 ? 's' : ''} ago`;
      }
    }
    return 'just now';
  }, [date]);

  useEffect(() => {
    setTimeAgo(calculate());
    const intervalId = setInterval(() => {
      setTimeAgo(calculate());
    }, ms);

    return () => {
      clearInterval(intervalId);
    }; // Clean up interval on unmount
  }, [calculate, ms]);

  return timeAgo;
}
